import React, { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { formatDistance } from 'date-fns';
import fr from 'date-fns/locale/fr';
import { config } from '../../config';

export default function CommentModal({ isOpen, onClose, post, currentUserId, onCommentAdded }) {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const messagesEndRef = useRef(null);

  const fetchComments = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiBase}/posts/get_comments.php?id_post=${post.id_post}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) setComments(data.comments);
    } catch (error) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) {
      toast.error('Écrivez un commentaire');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiBase}/posts/add_comment.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ id_post: post.id_post, commentaire: newComment })
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Commentaire ajouté');
        setNewComment('');
        fetchComments();
        if (onCommentAdded) onCommentAdded();
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      toast.error('Erreur');
    }
    setSubmitting(false);
  };

  const handleDeleteComment = async (id_comment) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiBase}/posts/delete_comment.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ id_comment })
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Commentaire supprimé');
        fetchComments();
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  useEffect(() => {
    if (isOpen) fetchComments();
  }, [isOpen]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [comments]);

  if (!isOpen) return null;

  const formatDate = (date) => formatDistance(new Date(date), new Date(), { addSuffix: true, locale: fr });

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden shadow-2xl">
        <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-r from-pink-400 to-purple-500 rounded-full flex items-center justify-center text-white font-bold">
              {post.prenom?.[0]}{post.nom?.[0]}
            </div>
            <div>
              <p className="font-semibold text-gray-800">{post.prenom} {post.nom}</p>
              <p className="text-xs text-gray-400 line-clamp-1 max-w-[200px]">{post.contenu}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
          {loading ? (
            <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div></div>
          ) : comments.length === 0 ? (
            <div className="text-center py-12"><div className="text-5xl mb-3">💬</div><p className="text-gray-400">Aucun commentaire</p></div>
          ) : (
            comments.map(comment => (
              <div key={comment.id_comment} className="flex gap-3">
                <div className="w-9 h-9 bg-gray-300 rounded-full flex items-center justify-center text-sm font-bold text-gray-600 flex-shrink-0">
                  {comment.prenom?.[0]}{comment.nom?.[0]}
                </div>
                <div className="flex-1 bg-white rounded-2xl p-3 shadow-sm">
                  <div className="flex justify-between items-start">
                    <div><span className="font-semibold text-sm">{comment.prenom} {comment.nom}</span><span className="text-xs text-gray-400 ml-2">{formatDate(comment.created_at)}</span></div>
                    {comment.id_utilisateur === currentUserId && (
                      <button onClick={() => handleDeleteComment(comment.id_comment)} className="text-gray-300 hover:text-red-500 text-xs">🗑️</button>
                    )}
                  </div>
                  <p className="text-gray-700 text-sm mt-1">{comment.commentaire}</p>
                </div>
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="sticky bottom-0 bg-white border-t p-4 flex gap-3">
          <input type="text" value={newComment} onChange={(e) => setNewComment(e.target.value)} placeholder="Écrire un commentaire..." className="flex-1 border rounded-full px-5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-pink-400" onKeyPress={(e) => e.key === 'Enter' && handleAddComment()} />
          <button onClick={handleAddComment} disabled={submitting || !newComment.trim()} className="bg-gradient-to-r from-pink-500 to-purple-600 text-white px-5 py-2.5 rounded-full text-sm font-semibold disabled:opacity-50">{submitting ? '...' : 'Envoyer'}</button>
        </div>
      </div>
    </div>
  );
}